import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useGetDelegationTask,
  useUpdateDelegationTask,
} from "services/delegation";
import { DelegationTask } from "protogen/delegations_pb";
import { UploadAttachment } from "protogen/common_pb";
import { PlainMessage } from "@bufbuild/protobuf";
import SubtaskDetail from "components/delegation/SubtaskDetail";
import { TimeEntry } from "components/common/TimeTracking";
import { UpdateDelegationTaskRequest } from "protogen/delegation_service_pb";
import { Family } from "protogen/advisors_service_pb";

export default () => {
  const { ref } = useParams<{ ref: string }>();
  const [task, setTask] = useState<DelegationTask | null>(null);
  const [familyInfo, setFamilyInfo] = useState<Family | null>(null);

  const { request: getTask, loading } = useGetDelegationTask((response) => {
    if (response.delegationTask) {
      setTask(response.delegationTask);
    }
    if (response.familyInfo) {
      setFamilyInfo(response.familyInfo);
    }
  });

  const { request: updateTask, loading: updating } = useUpdateDelegationTask(
    (response) => {
      if (response.delegationTask) {
        setTask(response.delegationTask);
      }
    },
  );

  useEffect(() => {
    if (ref) {
      getTask({ ref, markAsRead: true });
    }
  }, [ref]);

  const handleStatusChange = async (
    status: string,
    comment?: string,
    unassign?: boolean,
    attachments?: PlainMessage<UploadAttachment>[],
    actionDateSec?: number,
    recipientType?: string,
    commentHtml?: string,
  ) => {
    if (!task) return;
    await updateTask(
      new UpdateDelegationTaskRequest({
        ref: task.ref,
        claim: false,
        status,
        commentContent: comment || "",
        commentContentHtml: commentHtml || "",
        timeSpentMinutes: 0,
        unassign: unassign || false,
        attachments: attachments || [],
        timeEntriesJson: task.timeEntriesJson || "",
        actionDateSec: actionDateSec ? BigInt(actionDateSec) : undefined,
        recipientType: recipientType || "",
      }),
    );
  };

  const handleAddComment = async (
    content: string,
    attachments?: PlainMessage<UploadAttachment>[],
    recipientType?: string,
    contentHtml?: string,
  ) => {
    if (!task) return;
    await updateTask({
      ref: task.ref,
      claim: false,
      status: "",
      commentContent: content,
      commentContentHtml: contentHtml || "",
      timeSpentMinutes: 0,
      unassign: false,
      attachments: attachments || [],
      timeEntriesJson: task.timeEntriesJson || "",
      recipientType: recipientType || "",
    });
  };

  const handleSaveTimeEntries = async (entries: TimeEntry[]) => {
    if (!task) return;
    await updateTask({
      ref: task.ref,
      claim: false,
      status: "",
      commentContent: "",
      commentContentHtml: "",
      timeSpentMinutes: 0,
      unassign: false,
      attachments: [],
      timeEntriesJson: JSON.stringify(entries),
      recipientType: "",
    });
  };

  const handleRefresh = () => {
    if (ref) {
      getTask({ ref, markAsRead: true });
    }
  };

  return (
    <SubtaskDetail
      task={task}
      familyInfo={familyInfo}
      loading={loading}
      updating={updating}
      accountType="advisor"
      onStatusChange={handleStatusChange}
      onAddComment={handleAddComment}
      onSaveTimeEntries={handleSaveTimeEntries}
      onRefresh={handleRefresh}
      backPath="/tasks/subtasks"
      breadcrumbs={[
        { name: "Home", link: "/" },
        { name: "Tasks", link: "/tasks" },
      ]}
    />
  );
};
